import firebase from "firebase/app";
import { Roles } from "@evennt-tech/api-v2-core";
import { resetUser } from "store/auth/resetUser";
import { observabilityError, observabilityLogout } from "tools/observability";
import { resetCartStepper } from "store/exhibitors/cartStepper/slice";
import { resetExhibitorsImportProgress } from "store/organisers/bulkUpload/slice";
import { getRegisteredStore } from "store/store-registry";
import { resetProductsImportProgress } from "store/suppliers/bulkUpload/slice";
import { resetAllStores } from "store/utils/reset-store.utils";
import { IdleManager } from "tools/idle";
import { TOLGEE_LANGUAGE_STORAGE_KEY } from "tools/localization/constants";
import { stagedLanguageStore } from "store/localization/staged-language.store";
import { LAST_SIGN_IN_METHOD_STORAGE_KEY } from "tools/auth/lastLoginMethod";
import { LOGIN_PATH } from "view/routes";
import { queryClient } from "../../queryClient";
import {
  clearSessionSwitchInProgress,
  emitAuthSessionEvent,
  markSessionSwitchInProgress,
} from "./sessionSync";

const COOKIE_EXPIRED_AT = "Thu, 01 Jan 1970 00:00:00 GMT";
const AUTH_COOKIE_NAME_PARTS = [
  "auth",
  "token",
  "session",
  "login",
  "imperson",
  "refresh",
  "access",
];

type LogoutReason =
  | "manual"
  | "unauthorized"
  | "password-reset"
  | "login-error"
  | "fallback";

type LogoutFallbackReason =
  | "network-error"
  | "timeout"
  | "http-error"
  | "unknown-error";

type LogoutUserOptions = {
  reason: LogoutReason;
  redirect?: false;
  beforeCleanup?: () => Promise<void> | void;
  skipBackendLogout?: boolean;
  broadcast?: boolean;
};

const STORAGE_AUTH_KEY_PARTS = [
  "auth",
  "token",
  "session",
  "login",
  "imperson",
  "refresh",
  "access",
];
let resetAuthTransportsHandler: (() => void) | null = null;

export const registerAuthTransportReset = (handler: () => void) => {
  resetAuthTransportsHandler = handler;
};

const getAppVersionCookiePrefixes = () => {
  const rawValues = [
    import.meta.env.VITE_APP_VERSION,
    localStorage.getItem("ffair_v"),
  ].filter(Boolean) as string[];

  return Array.from(
    new Set(rawValues.map((value) => value.trim()).filter(Boolean))
  );
};

const getRootDomain = (hostname: string): string | null => {
  if (
    !hostname ||
    hostname === "localhost" ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)
  ) {
    return null;
  }

  const parts = hostname.split(".").filter(Boolean);

  if (parts.length < 2) {
    return null;
  }

  return parts.slice(-2).join(".");
};

const getCookieNamesToDelete = () => {
  const documentCookieNames = document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0]?.trim())
    .filter(Boolean) as string[];

  const versionedCookieNames = getAppVersionCookiePrefixes().flatMap(
    (prefix) => [`${prefix}_session_id`, `${prefix}_persistent_login`]
  );

  const knownCookieNames = [
    ...versionedCookieNames,
    "session_id",
    "persistent_login",
    "access_token",
    "refresh_token",
    "auth_token",
    "impersonation",
    "impersonation_token",
    "service_token",
  ];

  const authLikeCookieNames = documentCookieNames.filter((name) =>
    AUTH_COOKIE_NAME_PARTS.some((part) => name.toLowerCase().includes(part))
  );

  return Array.from(
    new Set([
      ...documentCookieNames,
      ...knownCookieNames,
      ...authLikeCookieNames,
    ])
  );
};

const clearStorageByPatterns = (
  storage: Storage,
  preservedKeys: string[] = []
) => {
  const keysToRemove: string[] = [];

  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);

    if (!key) {
      continue;
    }

    if (preservedKeys.includes(key)) {
      continue;
    }

    if (
      STORAGE_AUTH_KEY_PARTS.some((part) => key.toLowerCase().includes(part))
    ) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => {
    storage.removeItem(key);
  });

  storage.removeItem("ORGANISER_FROM_ADMIN");
};

const expireCookie = (cookieName: string, attributes: string[]) => {
  const suffix = attributes.length ? `; ${attributes.join("; ")}` : "";

  document.cookie = `${cookieName}=; Max-Age=0${suffix}`;
  document.cookie = `${cookieName}=; Expires=${COOKIE_EXPIRED_AT}${suffix}`;
};

const clearFrontendCookies = (cookieNames = getCookieNamesToDelete()) => {
  const { hostname, pathname } = window.location;
  const currentPath = pathname || "/";
  const rootDomain = getRootDomain(hostname);
  const attributeVariants = [
    [],
    ["path=/"],
    [`path=${currentPath}`],
    [`path=/`, `domain=${hostname}`],
    [`path=${currentPath}`, `domain=${hostname}`],
  ];

  if (rootDomain) {
    attributeVariants.push([`path=/`, `domain=.${rootDomain}`]);
    attributeVariants.push([`path=${currentPath}`, `domain=.${rootDomain}`]);
  }

  cookieNames.forEach((cookieName) => {
    attributeVariants.forEach((attributes) =>
      expireCookie(cookieName, attributes)
    );
  });
};

const clearClientAuthState = async (reason: LogoutReason) => {
  const cookieNamesToDelete = getCookieNamesToDelete();

  // A failed sign-in never had a session, so the visitor keeps the language
  // they picked on the login page.
  const isSignInError = reason === "login-error";

  // Capture the role before the store resets below wipe it — localisation is
  // exhibitor-only, so only exhibitors keep their language across logout.
  let isExhibitor = false;
  try {
    isExhibitor =
      getRegisteredStore().getState().auth?.role === Roles.EXHIBITOR;
  } catch (error) {
    console.warn("Failed to read role before logout cleanup", error);
  }

  try {
    resetAuthTransportsHandler?.();
  } catch (error) {
    console.warn("Failed to reset auth transports", error);
  }

  try {
    await queryClient.cancelQueries();
    queryClient.clear();
  } catch (error) {
    console.warn("Failed to clear query cache", error);
  }

  try {
    await firebase.auth().signOut();
  } catch (error) {
    console.warn("Failed to sign out Firebase auth", error);
  }

  try {
    resetAllStores();
    IdleManager.getInstance().clearSessionSubscribers();
  } catch (error) {
    console.warn("Failed to reset zustand stores", error);
  }

  try {
    const store = getRegisteredStore();
    store.dispatch(resetExhibitorsImportProgress());
    store.dispatch(resetProductsImportProgress());
    store.dispatch(resetCartStepper());
    store.dispatch(resetUser());
  } catch (error) {
    console.warn("Failed to reset redux auth state", error);
  }

  try {
    const preservedLanguage =
      isExhibitor || isSignInError
        ? localStorage.getItem(TOLGEE_LANGUAGE_STORAGE_KEY)
        : null;
    // The "last way in" hint has to outlive logout — it is needed on the NEXT
    // sign-in, which is the only time it is read. It is not auth state: no
    // identity, no token, just "google" or "password".
    const preservedLastSignInMethod = localStorage.getItem(
      LAST_SIGN_IN_METHOD_STORAGE_KEY
    );
    localStorage.clear();
    if (preservedLanguage) {
      localStorage.setItem(TOLGEE_LANGUAGE_STORAGE_KEY, preservedLanguage);
    }
    if (preservedLastSignInMethod) {
      localStorage.setItem(
        LAST_SIGN_IN_METHOD_STORAGE_KEY,
        preservedLastSignInMethod
      );
    }
  } catch (error) {
    console.warn("Failed to clear localStorage", error);
  }

  const { code: stagedCode, stage, clear } = stagedLanguageStore.getState();
  if (isSignInError && stagedCode) {
    stage(stagedCode);
  } else {
    clear();
  }

  try {
    sessionStorage.clear();
  } catch (error) {
    console.warn("Failed to clear sessionStorage", error);
  }

  // observabilityLogout already guards each provider independently.
  observabilityLogout();

  clearFrontendCookies(cookieNamesToDelete);
};

const getFallbackReason = (error: unknown): LogoutFallbackReason => {
  if (
    typeof DOMException !== "undefined" &&
    error instanceof DOMException &&
    error.name === "AbortError"
  ) {
    return "timeout";
  }

  if (error instanceof TypeError) {
    return "network-error";
  }

  return "unknown-error";
};

// Registered by `firebase.tsx` so we can call `apiV2.auth.logOut()` here
// without creating an import cycle (firebase.tsx already imports from this
// file). MUST be the auth-instrumented gateway method — the cookies
// (`_session_id`, `_persistent_login`) are HttpOnly, so only the server
// can clear them, and the request needs the `access-token` header to
// pass downstream Nest interceptors / observability.
let backendLogoutHandler: (() => Promise<unknown>) | null = null;

export const registerBackendLogout = (
  handler: () => Promise<unknown>
): void => {
  backendLogoutHandler = handler;
};

const requestBackendLogout = async (
  timeoutMs = 5000
): Promise<
  | { ok: true }
  | { ok: false; fallbackReason: LogoutFallbackReason; error?: unknown }
> => {
  if (!backendLogoutHandler) {
    return {
      ok: false,
      fallbackReason: "unknown-error",
      error: new Error("Backend logout handler is not registered"),
    };
  }

  let timeoutHandle: number | undefined;

  try {
    await Promise.race([
      backendLogoutHandler(),
      new Promise((_resolve, reject) => {
        timeoutHandle = window.setTimeout(
          () =>
            reject(
              typeof DOMException !== "undefined"
                ? new DOMException("Logout timed out", "AbortError")
                : new Error("Logout timed out")
            ),
          timeoutMs
        );
      }),
    ]);

    return { ok: true };
  } catch (error) {
    return { ok: false, fallbackReason: getFallbackReason(error), error };
  } finally {
    if (timeoutHandle !== undefined) {
      window.clearTimeout(timeoutHandle);
    }
  }
};

const reportLogoutFallback = (
  reason: LogoutReason,
  fallbackReason?: LogoutFallbackReason,
  error?: unknown
) => {
  if (!fallbackReason) {
    return;
  }

  const context = {
    reason,
    fallbackReason,
    pathname: window.location.pathname,
  };

  console.warn("Logout fallback cleanup executed", context, error);

  // observabilityError already guards each provider independently.
  observabilityError(
    error instanceof Error
      ? error
      : new Error(`Logout fallback cleanup executed: ${fallbackReason}`),
    context
  );
};

const redirectToLogin = (redirect?: false) => {
  if (redirect === false) {
    return;
  }

  window.location.replace(LOGIN_PATH);
};

// Dedup guard for concurrent `logoutUser` calls. A burst of 401s across
// several parallel requests used to fire `onUnauthorized` → `logoutUser`
// multiple times, racing cleanup (firebase signOut, store resets, navigation)
// against itself. First caller wins; subsequent callers join the same promise.
let pendingLogout: Promise<void> | null = null;

const runLogout = async ({
  reason,
  redirect,
  beforeCleanup,
  skipBackendLogout = false,
  broadcast = true,
}: LogoutUserOptions) => {
  // Emit the cross-tab `logout` event BEFORE we call the backend logout
  // and BEFORE `clearClientAuthState`. Two reasons:
  //   1. The backend logout destroys the SHARED session cookie. If a
  //      receiver tab has any in-flight protected request, it will 401
  //      between session destruction and our cross-tab event arriving —
  //      the receiver's `onUnauthorized` then races to `/login` before
  //      our `Apps` cross-tab handler can `beginNoticeRedirect()` and
  //      navigate it to `/session-changed`. Emitting first lets the
  //      receiver set the in-flight flag and start its hard-redirect
  //      while the backend session is still alive.
  //   2. `clearClientAuthState` itself wipes localStorage; the
  //      `AUTH_SESSION_EVENT_KEY` write happens before that wipe so the
  //      event reaches receivers cleanly.
  clearSessionSwitchInProgress();

  if (broadcast) {
    emitAuthSessionEvent("logout");
  }

  const logoutResult = skipBackendLogout
    ? { ok: true as const }
    : await requestBackendLogout();

  if (beforeCleanup) {
    try {
      await beforeCleanup();
    } catch (error) {
      console.warn("Logout pre-cleanup step failed", error);
    }
  }

  reportLogoutFallback(
    reason,
    logoutResult.ok ? undefined : logoutResult.fallbackReason,
    logoutResult.ok ? undefined : logoutResult.error
  );

  await clearClientAuthState(reason);
  redirectToLogin(redirect);
};

export const logoutUser = async (options: LogoutUserOptions) => {
  if (pendingLogout) {
    return pendingLogout;
  }

  pendingLogout = runLogout(options).finally(() => {
    pendingLogout = null;
  });

  return pendingLogout;
};

export const prepareSessionSwitch = async () => {
  markSessionSwitchInProgress();

  try {
    resetAuthTransportsHandler?.();
  } catch (error) {
    console.warn("Failed to reset auth transports for session switch", error);
  }

  try {
    await queryClient.cancelQueries();
    queryClient.clear();
  } catch (error) {
    console.warn("Failed to clear query cache for session switch", error);
  }

  try {
    await firebase.auth().signOut();
  } catch (error) {
    console.warn("Failed to sign out Firebase auth for session switch", error);
  }

  try {
    resetAllStores();
    IdleManager.getInstance().clearSessionSubscribers();
  } catch (error) {
    console.warn("Failed to reset zustand stores for session switch", error);
  }

  try {
    const store = getRegisteredStore();
    store.dispatch(resetExhibitorsImportProgress());
    store.dispatch(resetProductsImportProgress());
    store.dispatch(resetCartStepper());
    store.dispatch(resetUser());
  } catch (error) {
    console.warn("Failed to reset redux auth state for session switch", error);
  }
};
